'use client';

import type { BoxProps } from '@mui/material/Box';

import { useId, forwardRef } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { RouterLink } from '@/routes/components';
import { logoClasses } from '@/components/logo/classes';

// import { RouterLink } from 'src/routes/components';
//
// import { logoClasses } from './classes';

// ----------------------------------------------------------------------

export type LogoProps = BoxProps & {
  href?: string;
  isSingle?: boolean;
  disableLink?: boolean;
};

// eslint-disable-next-line react/display-name
export const Logo = forwardRef<HTMLDivElement, LogoProps>(
  (
    { width, href = '/', height, isSingle = true, disableLink = false, className, sx, ...other },
    ref
  ) => {
    const theme = useTheme();

    const gradientId = useId();

    const TEXT_PRIMARY = theme.vars.palette.text.primary;
    const PRIMARY_LIGHT = theme.vars.palette.primary.light;
    const PRIMARY_MAIN = theme.vars.palette.primary.main;
    const PRIMARY_DARKER = theme.vars.palette.primary.dark;

    /*
    * OR using local (public folder)
    *
    const singleLogo = (
      <Box
        alt="Single logo"
        component="img"
        src={`${CONFIG.assetsDir}/logo/logo-single.svg`}
        width="100%"
        height="100%"
      />
    );

    const fullLogo = (
      <Box
        alt="Full logo"
        component="img"
        src={`${CONFIG.assetsDir}/logo/logo-full.svg`}
        width="100%"
        height="100%"
      />
    );
    *
    */

    const singleLogo = (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 926.08 926.08"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g>
          <path
            fill={`url(#${gradientId}-single-main)`}
            d="M670.51,455.5c-26.53-0.95-89.73,10.13-89.73,10.13c6.27-27.02,65.13-145.68,65.13-145.68
          c0-20.27-30.4-29.91-30.4-29.91c-27.49,37.15-81.99,182.83-81.99,182.83c-63.2,8.2-133.61,23.15-133.61,23.15l59.82-132.17
          c3.38-14-29.91-26.04-29.91-26.04c-25.09,35.22-74.72,163.52-74.72,163.52c-18.33,3.38-147.17,33.78-147.17,33.78
          c-5.31,17.84,27.49,37.15,27.49,37.15c12.07-5.8,103.24-25.58,103.24-25.58c-12.53,20.27-68.97,210.81-68.97,210.81
          c-2.42,38.11,30.86,33.78,30.86,33.78c22.2-16.4,19.29-46.31,19.29-46.31c-3.38-1.93-3.38-9.64-3.38-9.64
          c4.33-21.71,67.04-198.74,67.04-198.74c24.11-8.2,136.03-22.66,136.03-22.66c-41.25,113.63-49.69,167.39-49.69,167.39
          c22.66,24.6,31.84,15.91,31.84,15.91L564,507.04l129.2-17.22C705.73,475.27,670.51,455.5,670.51,455.5z"
          />
          <path
            fill={`url(#${gradientId}-single-accent)`}
            d="M676.5,166.56c-8.82-3.09-18.46,1.52-21.56,10.34c-3.09,8.79,1.52,18.46,10.34,21.56
          c8.79,3.09,18.44-1.55,21.56-10.34C689.93,179.3,685.29,169.66,676.5,166.56z"
          />
          <path
            fill={`url(#${gradientId}-single-secondary)`}
            d="M686.76,137.38c-24.91-8.77-52.21,4.33-60.98,29.24c-3.17,9-3.48,18.28-1.42,26.89l15.42,77.4l0,0l0.26,1.26
          l0,0l0,0l13.67-11.81l46.57-40.2l0,0c1.8-1.39,3.51-2.94,5.11-4.59c1.5-1.55,2.91-3.22,4.2-5c2.66-3.66,4.85-7.76,6.45-12.27
          C724.76,173.45,711.67,146.14,686.76,137.38z M702.85,193.74c-6.21,17.66-25.55,26.94-43.19,20.73
          c-17.66-6.21-26.92-25.55-20.73-43.21c6.21-17.66,25.55-26.92,43.21-20.7C699.78,156.74,709.06,176.08,702.85,193.74z"
          />
        </g>
        <defs>
          <linearGradient
            id={`${gradientId}-single-main`}
            x1="300"
            y1="250"
            x2="650"
            y2="650"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_LIGHT} />
            <stop offset="1" stopColor={PRIMARY_MAIN} />
          </linearGradient>
          <linearGradient
            id={`${gradientId}-single-accent`}
            x1="650"
            y1="150"
            x2="690"
            y2="200"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_DARKER} />
            <stop offset="1" stopColor={PRIMARY_MAIN} />
          </linearGradient>
          <linearGradient
            id={`${gradientId}-single-secondary`}
            x1="640"
            y1="130"
            x2="720"
            y2="210"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_LIGHT} />
            <stop offset="1" stopColor={PRIMARY_DARKER} />
          </linearGradient>
        </defs>
      </svg>
    );

    const fullLogo = (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 926.08 926.08"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle fill={`url(#${gradientId}-full-bg)`} cx="463.04" cy="463.04" r="350.27" opacity="0.1" />
        <g>
          <path
            fill={`url(#${gradientId}-full-main)`}
            d="M670.51,455.5c-26.53-0.95-89.73,10.13-89.73,10.13c6.27-27.02,65.13-145.68,65.13-145.68
            c0-20.27-30.4-29.91-30.4-29.91c-27.49,37.15-81.99,182.83-81.99,182.83c-63.2,8.2-133.61,23.15-133.61,23.15l59.82-132.17
            c3.38-14-29.91-26.04-29.91-26.04c-25.09,35.22-74.72,163.52-74.72,163.52c-18.33,3.38-147.17,33.78-147.17,33.78
            c-5.31,17.84,27.49,37.15,27.49,37.15c12.07-5.8,103.24-25.58,103.24-25.58c-12.53,20.27-68.97,210.81-68.97,210.81
            c-2.42,38.11,30.86,33.78,30.86,33.78c22.2-16.4,19.29-46.31,19.29-46.31c-3.38-1.93-3.38-9.64-3.38-9.64
            c4.33-21.71,67.04-198.74,67.04-198.74c24.11-8.2,136.03-22.66,136.03-22.66c-41.25,113.63-49.69,167.39-49.69,167.39
            c22.66,24.6,31.84,15.91,31.84,15.91L564,507.04l129.2-17.22C705.73,475.27,670.51,455.5,670.51,455.5z"
          />
          <path
            fill={`url(#${gradientId}-full-accent)`}
            d="M676.5,166.56c-8.82-3.09-18.46,1.52-21.56,10.34c-3.09,8.79,1.52,18.46,10.34,21.56
            c8.79,3.09,18.44-1.55,21.56-10.34C689.93,179.3,685.29,169.66,676.5,166.56z"
          />
          <path
            fill={`url(#${gradientId}-full-secondary)`}
            d="M686.76,137.38c-24.91-8.77-52.21,4.33-60.98,29.24c-3.17,9-3.48,18.28-1.42,26.89l15.42,77.4l0,0l0.26,1.26
            l0,0l0,0l13.67-11.81l46.57-40.2l0,0c1.8-1.39,3.51-2.94,5.11-4.59c1.5-1.55,2.91-3.22,4.2-5c2.66-3.66,4.85-7.76,6.45-12.27
            C724.76,173.45,711.67,146.14,686.76,137.38z M702.85,193.74c-6.21,17.66-25.55,26.94-43.19,20.73
            c-17.66-6.21-26.92-25.55-20.73-43.21c6.21-17.66,25.55-26.92,43.21-20.7C699.78,156.74,709.06,176.08,702.85,193.74z"
          />
        </g>
        <defs>
          <linearGradient
            id={`${gradientId}-full-bg`}
            x1="113"
            y1="113"
            x2="813"
            y2="813"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_LIGHT} />
            <stop offset="1" stopColor={PRIMARY_MAIN} />
          </linearGradient>
          <linearGradient
            id={`${gradientId}-full-main`}
            x1="300"
            y1="250"
            x2="650"
            y2="650"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_LIGHT} />
            <stop offset="1" stopColor={PRIMARY_MAIN} />
          </linearGradient>
          <linearGradient
            id={`${gradientId}-full-accent`}
            x1="650"
            y1="150"
            x2="690"
            y2="200"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_DARKER} />
            <stop offset="1" stopColor={PRIMARY_MAIN} />
          </linearGradient>
          <linearGradient
            id={`${gradientId}-full-secondary`}
            x1="640"
            y1="130"
            x2="720"
            y2="210"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={PRIMARY_LIGHT} />
            <stop offset="1" stopColor={PRIMARY_DARKER} />
          </linearGradient>
        </defs>
      </svg>
    );

    const baseSize = {
      width: width ?? 40,
      height: height ?? 40,
      ...(!isSingle && {
        width: width ?? 102,
        height: height ?? 36,
      }),
    };

    return (
      <Box
        ref={ref}
        component={RouterLink}
        href={href}
        className={logoClasses.root.concat(className ? ` ${className}` : '')}
        aria-label="Logo"
        sx={{
          ...baseSize,
          flexShrink: 0,
          display: 'inline-flex',
          verticalAlign: 'middle',
          ...(disableLink && { pointerEvents: 'none' }),
          ...sx,
        }}
        {...other}
      >
        {isSingle ? singleLogo : fullLogo}
      </Box>
    );
  }
);
